import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getContracts, getContract, getUtilizationData } from '../../services/api';

const CONTRACT_TYPES = ['All', 'T&M', 'FFP', 'IDIQ'];

const PERIOD_ORDER = ['Base', 'OY1', 'OY2', 'OY3', 'OY4', 'OY5', 'OY6', 'OY7', 'OY8', 'OY9', 'Overall'];

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusBadgeClass(status) {
  if (!status) return '';
  switch (status) {
    case 'Active': return 'active';
    case 'Exercised': return 'exercised';
    case 'Not Exercised': return 'not-exercised';
    case 'Overall': return 'overall';
    case 'Proposed': return 'pending';
    default: return status.toLowerCase().replace(/\s+/g, '-');
  }
}

function sortPeriods(periods) {
  return [...periods].sort((a, b) => {
    const idxA = PERIOD_ORDER.indexOf(a.year);
    const idxB = PERIOD_ORDER.indexOf(b.year);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });
}

function computePopYears(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const s = new Date(startDate);
  const e = new Date(endDate);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
  const ms = e.getTime() - s.getTime();
  if (ms <= 0) return null;
  return ms / (1000 * 60 * 60 * 24 * 365.25);
}

// Column definitions for sorting
const COLUMNS = [
  { key: 'expand', label: '', sortable: false },
  { key: 'projectName', label: 'Project', sortable: true, type: 'string' },
  { key: 'contractNumber', label: 'Contract #', sortable: true, type: 'string' },
  { key: 'division', label: 'Division', sortable: true, type: 'string' },
  { key: 'contractType', label: 'Type', sortable: true, type: 'string' },
  { key: 'customer', label: 'Customer', sortable: true, type: 'string' },
  { key: 'endCustomer', label: 'End Customer', sortable: true, type: 'string' },
  { key: 'contractValue', label: 'Contract Value', sortable: true, type: 'number' },
  { key: 'totalFundingITD', label: 'Funded ITD', sortable: true, type: 'number' },
  { key: 'startDate', label: 'Start Date', sortable: true, type: 'date' },
  { key: 'endDate', label: 'End Date', sortable: true, type: 'date' },
  { key: 'status', label: 'Status', sortable: true, type: 'string' },
];

function compareValues(a, b, key, type) {
  let valA = a[key];
  let valB = b[key];

  if (type === 'number') {
    valA = Number(valA) || 0;
    valB = Number(valB) || 0;
    return valA - valB;
  }
  if (type === 'date') {
    const dA = valA ? new Date(valA).getTime() : 0;
    const dB = valB ? new Date(valB).getTime() : 0;
    return dA - dB;
  }
  // string
  return (valA || '').toLowerCase().localeCompare((valB || '').toLowerCase());
}

function Contracts({ isAdmin, searchQuery, onNavigate }) {
  const [contracts, setContracts] = useState([]);
  const [uteData, setUteData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState(searchQuery || '');
  const [divisionFilter, setDivisionFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [customerFilter, setCustomerFilter] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  const [sortKey, setSortKey] = useState('division');
  const [sortDir, setSortDir] = useState('asc');

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedContract, setExpandedContract] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);

  const searchRef = useRef(null);

  // Cmd+K / Ctrl+K keyboard shortcut to focus search
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const [contractData, uteResult] = await Promise.all([
          getContracts(),
          getUtilizationData('2026').catch(() => ({ employees: [] })),
        ]);
        setContracts(contractData);
        setUteData(uteResult.employees || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const customers = React.useMemo(() => {
    const set = new Set();
    contracts.forEach((c) => {
      const ec = c.endCustomer || c.customer;
      if (ec) set.add(ec);
    });
    return ['All', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [contracts]);

  // Dynamic division list derived from actual contract data
  const divisions = React.useMemo(() => {
    const set = new Set();
    contracts.forEach((c) => { if (c.division) set.add(c.division); });
    return ['All', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [contracts]);

  // Aggregate UTE metrics for summary bar — weighted: sum(billableHrs) / sum(availHrs)
  const uteSummary = React.useMemo(() => {
    if (uteData.length === 0) return null;
    const headcount = uteData.length;
    const totalBillableHrs = uteData.reduce((sum, e) => sum + (e.billableHours || 0), 0);
    const totalAvailHrs = uteData.reduce((sum, e) => sum + (e.totalHoursAvailable || 0), 0);
    const avgBillableUte = totalAvailHrs > 0 ? (totalBillableHrs / totalAvailHrs) * 100 : 0;
    return { headcount, avgBillableUte };
  }, [uteData]);

  // Per-division breakdown combining contracts + utilization
  const divisionInsights = React.useMemo(() => {
    const contractsByDiv = {};
    contracts.forEach((c) => {
      const div = c.division || 'Unknown';
      if (!contractsByDiv[div]) contractsByDiv[div] = [];
      contractsByDiv[div].push(c);
    });

    const uteByDiv = {};
    uteData.forEach((e) => {
      const div = e.division || 'Unknown';
      if (!uteByDiv[div]) uteByDiv[div] = [];
      uteByDiv[div].push(e);
    });

    const allDivs = new Set([...Object.keys(contractsByDiv), ...Object.keys(uteByDiv)]);
    allDivs.delete('Unknown');

    return Array.from(allDivs).sort().map((div) => {
      const divContracts = contractsByDiv[div] || [];
      const divEmployees = uteByDiv[div] || [];
      return {
        division: div,
        contractCount: divContracts.length,
        portfolioValue: divContracts.reduce((sum, c) => sum + (Number(c.contractValue) || 0), 0),
        fundedITD: divContracts.reduce((sum, c) => sum + (Number(c.totalFundingITD) || 0), 0),
        headcount: divEmployees.length,
        totalBillableHrs: divEmployees.reduce((sum, e) => sum + (e.billableHours || 0), 0),
        avgBillableUte: (() => {
          const avail = divEmployees.reduce((sum, e) => sum + (e.totalHoursAvailable || 0), 0);
          const bill = divEmployees.reduce((sum, e) => sum + (e.billableHours || 0), 0);
          return avail > 0 ? (bill / avail) * 100 : 0;
        })(),
      };
    });
  }, [contracts, uteData]);

  const activeFilterCount = (divisionFilter !== 'All' ? 1 : 0)
    + (typeFilter !== 'All' ? 1 : 0)
    + (customerFilter !== 'All' ? 1 : 0);

  const handleSort = useCallback((key) => {
    if (key !== sortKey) {
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey('division');
      setSortDir('asc');
    }
  }, [sortKey, sortDir]);

  const handleClearFilters = useCallback(() => {
    setDivisionFilter('All');
    setTypeFilter('All');
    setCustomerFilter('All');
  }, []);

  const filtered = React.useMemo(() => {
    const colDef = COLUMNS.find((c) => c.key === sortKey);
    const type = colDef ? colDef.type : 'string';

    const list = contracts.filter((c) => {
      if (divisionFilter !== 'All' && c.division !== divisionFilter) return false;
      if (typeFilter !== 'All' && c.contractType !== typeFilter) return false;
      if (customerFilter !== 'All' && (c.endCustomer || c.customer) !== customerFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const match =
          (c.projectName || '').toLowerCase().includes(q) ||
          (c.contractNumber || '').toLowerCase().includes(q) ||
          (c.program || '').toLowerCase().includes(q) ||
          (c.division || '').toLowerCase().includes(q) ||
          (c.customer || '').toLowerCase().includes(q) ||
          (c.endCustomer || '').toLowerCase().includes(q) ||
          (c.contractType || '').toLowerCase().includes(q) ||
          (c.status || '').toLowerCase().includes(q) ||
          (c.startDate || '').includes(q) ||
          (c.endDate || '').includes(q);
        if (!match) return false;
      }
      return true;
    });

    list.sort((a, b) => {
      const cmp = compareValues(a, b, sortKey, type);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    setCurrentPage(1);

    return list;
  }, [contracts, divisionFilter, typeFilter, customerFilter, search, sortKey, sortDir]);

  const totalPages = pageSize === 0 ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = pageSize === 0 ? filtered : filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const rangeStart = filtered.length === 0 ? 0 : (pageSize === 0 ? 1 : (currentPage - 1) * pageSize + 1);
  const rangeEnd = pageSize === 0 ? filtered.length : Math.min(currentPage * pageSize, filtered.length);

  const summary = React.useMemo(() => {
    let totalValue = 0;
    let totalFunding = 0;
    let weightedFunding = 0;
    let totalYears = 0;

    filtered.forEach((c) => {
      totalValue += Number(c.contractValue) || 0;
      const funding = Number(c.totalFundingITD) || 0;
      totalFunding += funding;
      const popYears = computePopYears(c.startDate, c.endDate);
      if (popYears && funding) {
        weightedFunding += funding;
        totalYears += popYears;
      }
    });

    const arr = totalYears > 0 ? weightedFunding / totalYears : 0;

    return { count: filtered.length, totalValue, totalFunding, arr };
  }, [filtered]);

  const handleRowClick = useCallback(async (contractNumber) => {
    if (expandedContract === contractNumber) {
      setExpandedContract(null);
      setPeriods([]);
      return;
    }
    setExpandedContract(contractNumber);
    setPeriodsLoading(true);
    setPeriods([]);
    try {
      const data = await getContract(contractNumber);
      const nonOverall = data.filter((p) => p.year !== 'Overall');
      setPeriods(sortPeriods(nonOverall));
    } catch (err) {
      setPeriods([]);
    } finally {
      setPeriodsLoading(false);
    }
  }, [expandedContract]);

  return (
    <div>
      {/* Search Bar + Filters Toggle + New Contract */}
      <div className="contracts-search-row">
        <div className="contracts-search-bar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search by contract #, project, customer, division, type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <kbd className="search-shortcut">&#8984;K</kbd>
        </div>
        <button
          className={`btn btn-outline filter-toggle-btn${showFilters ? ' active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="11" y1="18" x2="13" y2="18" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="filter-badge">{activeFilterCount}</span>
          )}
        </button>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => onNavigate('new-contract')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Contract
          </button>
        )}
      </div>

      {/* Collapsible Filter Panel */}
      {showFilters && (
        <div className="contracts-filter-panel">
          <select
            className="filter-select"
            value={divisionFilter}
            onChange={(e) => setDivisionFilter(e.target.value)}
          >
            {divisions.map((d) => (
              <option key={d} value={d}>{d === 'All' ? 'All Divisions' : d}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {CONTRACT_TYPES.map((t) => (
              <option key={t} value={t}>{t === 'All' ? 'All Types' : t}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
          >
            {customers.map((c) => (
              <option key={c} value={c}>{c === 'All' ? 'All End Customers' : c}</option>
            ))}
          </select>
          {activeFilterCount > 0 && (
            <button className="btn btn-outline btn-sm" onClick={handleClearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Summary Bar */}
      {!loading && !error && (
        <div className="contracts-summary">
          <div className="contracts-summary-item">
            <span className="contracts-summary-label">Contracts</span>
            <span className="contracts-summary-value">{summary.count}</span>
          </div>
          <div className="contracts-summary-item">
            <span className="contracts-summary-label">Portfolio Value</span>
            <span className="contracts-summary-value">{formatCurrency(summary.totalValue)}</span>
          </div>
          <div className="contracts-summary-item">
            <span className="contracts-summary-label">Total Funded ITD</span>
            <span className="contracts-summary-value">{formatCurrency(summary.totalFunding)}</span>
          </div>
          <div className="contracts-summary-item">
            <span className="contracts-summary-label">Annual Recurring Revenue</span>
            <span className="contracts-summary-value accent">{formatCurrency(summary.arr)}</span>
          </div>
          {uteSummary && (
            <>
              <div className="contracts-summary-divider" />
              <div className="contracts-summary-item">
                <span className="contracts-summary-label">Headcount</span>
                <span className="contracts-summary-value">{uteSummary.headcount}</span>
              </div>
              <div className="contracts-summary-item">
                <span className="contracts-summary-label">Avg Billable Ute</span>
                <span className="contracts-summary-value accent">{uteSummary.avgBillableUte.toFixed(1)}%</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Division Overview — contracts + utilization per division */}
      {!loading && !error && divisionInsights.length > 0 && (
        <div className="division-overview">
          <div className="division-overview-header">
            <span className="division-overview-title">Division Overview</span>
            <span className="division-overview-sub">Contracts + Utilization by Division</span>
          </div>
          <div className="division-overview-grid">
            {divisionInsights.map((d) => (
              <div key={d.division} className="division-overview-card">
                <div className="division-overview-name">{d.division}</div>
                {d.contractCount > 0 && (
                  <div className="division-overview-group">
                    <div className="division-overview-group-label">Contracts</div>
                    <div className="division-overview-row">
                      <span className="division-overview-label">Count</span>
                      <span className="division-overview-value">{d.contractCount}</span>
                    </div>
                    <div className="division-overview-row">
                      <span className="division-overview-label">Portfolio</span>
                      <span className="division-overview-value">{formatCurrency(d.portfolioValue)}</span>
                    </div>
                    <div className="division-overview-row">
                      <span className="division-overview-label">Funded ITD</span>
                      <span className="division-overview-value">{formatCurrency(d.fundedITD)}</span>
                    </div>
                  </div>
                )}
                {d.headcount > 0 && (
                  <div className="division-overview-group">
                    <div className="division-overview-group-label">Delivery</div>
                    <div className="division-overview-row">
                      <span className="division-overview-label">Headcount</span>
                      <span className="division-overview-value">{d.headcount}</span>
                    </div>
                    <div className="division-overview-row">
                      <span className="division-overview-label">Avg Billable Ute</span>
                      <span className="division-overview-value accent">{d.avgBillableUte.toFixed(1)}%</span>
                    </div>
                    <div className="division-overview-row">
                      <span className="division-overview-label">Billable Hrs</span>
                      <span className="division-overview-value">{d.totalBillableHrs.toLocaleString()}</span>
                    </div>
                  </div>
                )}
                {d.contractCount === 0 && d.headcount === 0 && (
                  <div className="division-overview-row">
                    <span className="division-overview-value dim">No data</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="data-table-wrapper">
        {loading && (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--bcore-text-dim)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '13px' }}>
            Loading contracts...
          </div>
        )}
        {error && (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--status-critical)', fontSize: '13px' }}>
            {error}
          </div>
        )}
        {!loading && !error && (
          <table className="data-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className={col.sortable ? 'sortable-th' : ''}
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  >
                    {col.label}
                    {col.sortable && sortKey === col.key && (
                      <span className="sort-indicator">
                        {sortDir === 'asc' ? ' \u25B2' : ' \u25BC'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((c) => {
                const isExpanded = expandedContract === c.contractNumber;
                return (
                  <React.Fragment key={c.PK + c.SK}>
                    <tr
                      className={`contract-row${isExpanded ? ' expanded' : ''}`}
                      onClick={() => handleRowClick(c.contractNumber)}
                    >
                      <td className="expand-cell">
                        <svg
                          className={`expand-icon${isExpanded ? ' open' : ''}`}
                          width="12" height="12" viewBox="0 0 24 24"
                          fill="none" stroke="currentColor" strokeWidth="2"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </td>
                      <td className="cell-primary">{c.projectName || '--'}</td>
                      <td>{c.contractNumber || '--'}</td>
                      <td>{c.division || '--'}</td>
                      <td>{c.contractType || '--'}</td>
                      <td>{c.customer || '--'}</td>
                      <td>{c.endCustomer || '--'}</td>
                      <td>{formatCurrency(c.contractValue)}</td>
                      <td>{formatCurrency(c.totalFundingITD)}</td>
                      <td>{formatDate(c.startDate)}</td>
                      <td>{formatDate(c.endDate)}</td>
                      <td>
                        <span className={`badge-status ${statusBadgeClass(c.status)}`}>
                          <span className="badge-dot"></span>
                          {c.status || '--'}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="periods-container-row">
                        <td colSpan="12">
                          {periodsLoading ? (
                            <div className="periods-loading">Loading periods...</div>
                          ) : periods.length === 0 ? (
                            <div className="periods-loading">No period data found</div>
                          ) : (
                            <table className="periods-table">
                              <thead>
                                <tr>
                                  <th>Period</th>
                                  <th>Status</th>
                                  <th>Start Date</th>
                                  <th>End Date</th>
                                  <th>Contract Value</th>
                                  <th>Funding/Year</th>
                                  <th>Funded ITD</th>
                                </tr>
                              </thead>
                              <tbody>
                                {periods.map((p) => {
                                  const periodClass =
                                    p.status === 'Active' ? 'period-active' :
                                    p.status === 'Exercised' ? 'period-exercised' :
                                    p.status === 'Not Exercised' ? 'period-not-exercised' : '';
                                  return (
                                    <tr key={p.SK} className={`period-row ${periodClass}`}>
                                      <td className="period-year">{p.year || '--'}</td>
                                      <td>
                                        <span className={`badge-status ${statusBadgeClass(p.status)}`}>
                                          <span className="badge-dot"></span>
                                          {p.status || '--'}
                                        </span>
                                      </td>
                                      <td>{formatDate(p.startDate)}</td>
                                      <td>{formatDate(p.endDate)}</td>
                                      <td>{formatCurrency(p.contractValue)}</td>
                                      <td>{formatCurrency(p.fundingPerYear)}</td>
                                      <td>{formatCurrency(p.totalFundingITD)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {paged.length === 0 && (
                <tr>
                  <td colSpan="13" style={{ textAlign: 'center', padding: '40px', color: 'var(--bcore-text-dim)' }}>
                    No contracts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && filtered.length > 0 && (
        <div className="pagination-bar">
          <div className="pagination-info">
            Showing {rangeStart}–{rangeEnd} of {filtered.length}
          </div>
          <div className="pagination-controls">
            <button
              className="btn btn-outline btn-sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="pagination-page">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-outline btn-sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
          <div className="pagination-size">
            <span className="pagination-size-label">Rows per page</span>
            <select
              className="filter-select"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={0}>All</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contracts;
